import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import constant from "config/constant";
import { secondsToMilliseconds } from "date-fns";
import { wrapBaseApi } from "util/apiWrapper";
import ls from "util/secureStorage";
import { get } from "../../util/axiosLib";

const getToken = () => ls.get(constant.AUTH_TOKEN_KEY).token;

const getHeaders = (token = null) => ({
  authorization: token ?? getToken(),
  "Content-Type": "application/json",
});

const initialState = {
  status: "idle",
  // verification: [],
  auth: {
    hasShownPopup: false,
    token: null,
    isSignedIn: false,
    exp: null, // token expiration in seconds.
    isVerified: false,
    isEmailVerified: false,
    isPhoneVerified: false,
    verification: [],
  },
  profile: {
    status: "idle",
    data: {},
    verification: [],
  },
  countries: {
    status: "idle",
    data: [],
  },
  checks: {
    email: {
      status: "idle",
      inUse: false,
    },
    username: {
      status: "idle",
      inUse: false,
    },
    phone: {
      status: "idle",
      inUse: false,
    },
  },
};

export const fetchUserProfile = createAsyncThunk(
  "auth/fetchUserProfile",
  async (token, _) => {
    const response = await get(
      wrapBaseApi(`/auth/currentUser`),
      getHeaders(token)
    );
    return response.data;
  }
);

export const fetchCountries = createAsyncThunk(
  "auth/fetchCountries",
  async () => {
    const response = await axios.get(
      wrapBaseApi(`/countries`)
    );

    return response.data;
  }
);

export const checkEmail = createAsyncThunk(
  "auth/checkEmail",
  async (email, _) => {
    const response = await get(
      wrapBaseApi(`/auth/check?email=${email}`)
    );
    return response.data;
  }
);

export const checkUsername = createAsyncThunk(
  "auth/checkUsername",
  async (username, _) => {
    const response = await get(
      wrapBaseApi(`/auth/check?username=${username}`)
    );
    return response.data;
  }
);

export const checkPhone = createAsyncThunk(
  "auth/checkPhone",
  async (phone, _) => {
    const response = await get(
      wrapBaseApi(`/auth/check?phone=${phone}`)
    );
    return response.data;
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    hydrateToken: (state, action) => {
      const auth = {
        token: action?.payload?.token,
        exp: action?.payload?.exp ?? null,
        isSignedIn: action?.payload?.isSignedIn ?? true,
        verification: action?.payload?.verification ?? [],
      };
      state.auth = { ...auth };

      ls.set(constant.AUTH_TOKEN_KEY, auth);
    },

    dehydrate: (state, action) => {
      state = { ...initialState };
      ls.remove(constant.AUTH_TOKEN_KEY);
    },
    
    updatePopUpShow: (state, action) => {
      state.auth.hasShownPopup = action.payload.hasShownPopup
    },

    persistTemporaryProfile: (state, action) => {
      state.profile.data = action.payload;

      ls.set(constant.TEMP_PROFILE, action.payload);
    },
  },

  extraReducers: (builder) => {
    builder.addCase(fetchCountries.fulfilled, (state, { payload }) => {
      state.countries.status = "success";
      state.countries.data = payload;
      // state.profile.country = "Ghana";
    });

    builder.addCase(fetchCountries.pending, (state, action) => {
      state.countries.status = "fetching";
    });

    builder.addCase(fetchCountries.rejected, (state, action) => {
      state.countries.status = "error";
    });

    builder.addCase(fetchUserProfile.fulfilled, (state, { payload }) => {
      state.profile.status = "success";
      state.profile.data = payload.data;

      state.profile.verification = payload.verification;
      state.auth.verification = payload.verification;

      state.auth = {
        ...state?.auth,
        exp: secondsToMilliseconds(payload?.data?.exp),
        isSignedIn:
          payload?.data?.loginStatus === "complete" ||
          payload?.data?.loginStatus === "google"
            ? true
            : false,
        isEmailVerified: payload?.data?.emailVerified,
        isPhoneVerified: payload?.data?.isVerified,
        verification: payload?.data?.verification,
      };
      ls.set(constant.AUTH_TOKEN_KEY, state.auth);
      // state.profile.country = "Ghana";
    });
    builder.addCase(fetchUserProfile.pending, (state, action) => {
      state.profile.status = "fetching";
    });
    builder.addCase(fetchUserProfile.rejected, (state, action) => {
      state.profile.status = "error";
    });
    builder.addCase(checkEmail.fulfilled, (state, { payload }) => {
      state.checks.email.status = "success";
      state.checks.email.inUse = false;
      // state.profile.country = "Ghana";
    });
    builder.addCase(checkEmail.pending, (state, action) => {
      state.checks.email.status = "fetching";
    });
    builder.addCase(checkEmail.rejected, (state, action) => {
      state.checks.email.status = "error";
      state.checks.email.inUse = true;
    });
    builder.addCase(checkUsername.fulfilled, (state, { payload }) => {
      state.checks.username.status = "success";
      state.checks.username.inUse = false;
      // state.profile.country = "Ghana";
    });
    builder.addCase(checkUsername.pending, (state, action) => {
      state.checks.username.status = "fetching";
    });
    builder.addCase(checkUsername.rejected, (state, action) => {
      state.checks.username.status = "error";
      state.checks.username.inUse = true;
    });
  },
});

export const selectAuth = (state) => state.auth;

export const { actions, reducer: AuthReducer } = authSlice;
export const { hydrateToken, dehydrate, updatePopUpShow, persistTemporaryProfile } = actions;
